import { ApolloError } from '@apollo/client';
import { EquipmentCategory } from '__generated__/graphql';
import classNames from 'classnames';
import BasicSearchablePicker from 'components/foundation/pickers/basic-picker/BasicSearchablePicker/BasicSearchablePicker';
import useEquipmentCategories from 'containers/manage-euipment/hooks/useEquipmentCategories';
import { useOrganization } from 'hooks';
import useOnMount from 'hooks/utils/useOnMount/useOnMount';
import { t } from 'i18next';
import isNil from 'lodash/isNil';
import uniqBy from 'lodash/uniqBy';
import { useEffect, useState } from 'react';
import { IBasicPickerProps } from '../../../foundation/pickers/basic-picker/BasicPicker/BasicPicker';

export type IEquipmentCategoryPickerProps = Omit<IBasicPickerProps, 'data'> & {
  reload?: boolean;
};

const EquipmentCategoryPicker = ({ className, reload, error, ...rest }: IEquipmentCategoryPickerProps) => {
  const [data, setData] = useState<EquipmentCategory[]>([]);
  const [graphqlError, setGraphqlError] = useState<string | ApolloError | null | undefined>(null);
  const { getEquipmentCategories } = useEquipmentCategories();

  useOnMount(() => {
    getData();
  });

  useEffect(() => {
    if (reload) {
      getData();
    }
  }, [reload]);

  const getData = async () => {
    try {
      const data = await getEquipmentCategories();
      setData(data);
    } catch (error) {
      setGraphqlError(t('There was an error loading categories.'));
    }
  };

  const parsed = uniqBy(
    data?.map((datum) => ({ id: datum.id, name: datum.title! })),
    (category) => category.id
  );

  const classes = classNames('equipment-category-picker', className);

  return (
    <BasicSearchablePicker
      {...rest}
      data={parsed}
      className={classes}
      error={error || (!isNil(graphqlError) && graphqlError !== '')}
    />
  );
};

const EquipmentCategoryPickerContainer = (props: IEquipmentCategoryPickerProps) => {
  const organization = useOrganization();
  if (!organization.trackEquipment) {
    return null;
  }

  return <EquipmentCategoryPicker {...props} />;
};

export default EquipmentCategoryPickerContainer;
